import React, { useCallback, useMemo } from 'react';
import cx from 'classnames';
import { RichInputVariant } from '@mutiny-pkg/champaign-schemas/src/schemas/richText';
import {
  FlexibleTextContent,
  TextTreeNodeData,
  StreamingTextTreeNodeData,
} from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { MentionItem } from '@mutiny-pkg/bin-ui/src/richtext/MentionsPlugin/MentionsPlugin';
import {
  hydrateFlexibleTextVariables,
  isEmptyFlexibleText,
  isFlexibleTextEqual,
  normalizeStreamingFlexibleText,
  renderFlexibleTextToHtml,
} from '../../richtext/flexibleTextAst';
import { ContentEditable } from '@mutiny-pkg/bin-ui/src/richtext/ContentEditable/ContentEditable';
import { LexicalThemeColors } from '../../richtext/types';
import {
  convertHexToTailwindClass,
  lexicalThemeColorsToThemeColors,
} from '@mutiny-pkg/bin-ui/src/utils/colorConversion';
import { updateClassString, EDITOR_BREAKPOINTS } from '@mutiny-pkg/tailwind-parser';
import { transformToContainerQueries } from '../utils/transformBreakpointClasses';
import './Text.css';
import { FlexibleTextInput } from '@mutiny-pkg/bin-ui/src/richtext/FlexibleTextInput/FlexibleTextInput';

const [BASE_BREAKPOINT] = EDITOR_BREAKPOINTS;

type DefaultElement = React.ElementType<any, 'span'>;

type PolymorphicTextProps<T extends React.ElementType = DefaultElement> = {
  as?: T;
  id: string;
  editorClassName?: string;
  onChange?: (id: string, data: Partial<TextTreeNodeData>) => void;
  mentionItems: MentionItem[];
  refetchMentionItems: () => Promise<void>;
  mentionItemsEmptyMessage?: string;
  overrideVariables?: Partial<Record<string, string>>;
  isEditor: boolean;
  themeColors?: LexicalThemeColors;
} & StreamingTextTreeNodeData &
  Omit<React.ComponentPropsWithoutRef<T>, 'onChange' | 'onClick'>;

type PolymorphicRef<T extends React.ElementType = DefaultElement> = React.ComponentPropsWithRef<T>['ref'];

function TextInner<T extends React.ElementType = DefaultElement>(
  {
    as,
    className,
    editorClassName,
    text,
    variant,
    id,
    onChange,
    mentionItems,
    refetchMentionItems,
    mentionItemsEmptyMessage,
    overrideVariables,
    onClick,
    onDoubleClick,
    isDoubleClickActive,
    style,
    isEditor,
    themeColors,
    ...props
  }: PolymorphicTextProps<T>,
  ref: PolymorphicRef<T>,
) {
  const isEditing = isDoubleClickActive;
  let Element: React.ElementType = 'span' as DefaultElement;
  if (as) {
    Element = as;
  }

  // Normalize to FlexibleTextContent for safe processing (handles legacy RichText and streaming data)
  const normalizedText = useMemo(() => normalizeStreamingFlexibleText(text), [text]);

  // Generate variant class from data.variant, or check if it's already in className
  const variantClassName = useMemo(() => {
    if (variant) {
      return `text-${variant}`;
    }
    // Check if className already has a text variant class (backward compatibility)
    if (typeof className === 'string' && className.match(/text-(body|heading1|heading2|heading3|heading4|overline)/)) {
      return undefined; // variant already in className, don't duplicate
    }
    return undefined;
  }, [variant, className]);

  // Combine base className with variant, then transform for container queries (editor preview)
  const combinedClassName = cx(transformToContainerQueries(className, { isEditor }), variantClassName);

  const hydratedText = useMemo(() => {
    return hydrateFlexibleTextVariables(normalizedText, overrideVariables || {}, isEditor);
  }, [normalizedText, overrideVariables, isEditor]);

  const renderedHtml = useMemo(() => {
    return renderFlexibleTextToHtml(hydratedText, undefined, { isEditor, mentionItems });
  }, [hydratedText, isEditor, mentionItems]);

  const handleDoubleClick = useCallback(
    (event: React.MouseEvent<T>) => {
      onDoubleClick?.(event);
    },
    [onDoubleClick],
  );

  const handlePropagation = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  const { onBlur, ...restProps } = props;

  const handleChange = useCallback(
    (newValue: FlexibleTextContent) => {
      if (isFlexibleTextEqual(newValue, normalizedText)) return;
      onChange?.(id, { text: newValue });
    },
    [id, normalizedText, onChange],
  );

  const handleColorChange = useCallback(
    (color: string, isFullSelection: boolean, type: 'text' | 'highlight'): boolean => {
      if (!isFullSelection || type !== 'text') {
        return true;
      }

      const themeColorArray = lexicalThemeColorsToThemeColors(themeColors);
      const newColorClass = convertHexToTailwindClass(color, themeColorArray, 'text-');

      // Use updateClassString to update textColor property (non-breakpoint-aware, always base)
      const newClassNameString = updateClassString({
        classString: className || '',
        property: 'textColor',
        breakpoint: BASE_BREAKPOINT,
        value: newColorClass,
      });

      onChange?.(id, { className: newClassNameString });

      return false;
    },
    [className, themeColors, id, onChange],
  );

  const isEmpty = isEmptyFlexibleText(normalizedText);

  // Only render empty placeholder in editor mode, not in preview/publish
  const shouldShowEmptyPlaceholder = isEmpty && isEditor;

  return (
    <div
      id={id}
      ref={ref}
      className={cx(combinedClassName, editorClassName, 'mutiny-text-component-wrapper')}
      data-no-dnd={isEditing}
    >
      {isEditing ? (
        <div
          data-lexical-editor
          style={{ cursor: 'text', ...style }}
          onClick={handlePropagation}
          onDoubleClick={handlePropagation}
          {...restProps}
        >
          <FlexibleTextInput
            value={normalizedText}
            onChange={handleChange}
            onBlur={onBlur}
            onColorChange={handleColorChange}
            debounced
            editable
            enableTextAlign={false}
            enableExtendedTextFormatting
            variant={RichInputVariant.InlineEditing}
            autoFocus
            mentionItems={mentionItems}
            onMentionMenuOpen={refetchMentionItems}
            mentionItemsEmptyMessage={mentionItemsEmptyMessage}
            enableFloatingMenu
            themeColors={themeColors}
            onKeyDown={{
              Escape: (e) => {
                if (isEditing) {
                  e.preventDefault();
                  e.stopPropagation();
                  onBlur?.();
                }
                return false;
              },
            }}
          >
            <ContentEditable ariaLabel="Text" placeholder="Enter text" />
          </FlexibleTextInput>
        </div>
      ) : (
        <Element
          data-lexical-editor
          onClick={onClick}
          onDoubleClick={handleDoubleClick}
          style={style}
          dangerouslySetInnerHTML={{
            __html: shouldShowEmptyPlaceholder
              ? '<p class="mutiny-text-empty-placeholder">Enter text</p>'
              : renderedHtml,
          }}
          {...props}
        />
      )}
    </div>
  );
}

TextInner.displayName = 'Text';

export const Text = React.forwardRef<PolymorphicRef<React.ElementType>, PolymorphicTextProps>(
  TextInner,
) as typeof TextInner;
